/**
 * Smooth horses between the server's ~10 Hz position frames (ADR-0016). Each frame carries the race clock (ms since
 * the start), so frames are placed on the server's own timeline and drawn a little in the past (`delayMs`), blending
 * between the two frames around that moment: late or bunched packets do not make the horses stutter. The local-time
 * offset follows the fastest delivery seen (and drifts up slowly if the network gets slower).
 */
export interface TimelineFrame {
  clock: number;
  /** Metres run per horse. */
  m: number[];
}

export interface TimelineSample {
  /** Metres run per horse at the sampled moment. */
  m: number[];
  /** Speed per horse (m/s), for the gallop animation. */
  v: number[];
}

const KEEP_MS = 3000;
/** Past the newest frame, keep moving at the last speed for at most this long (a late packet), then hold. */
const MAX_EXTRAPOLATE_MS = 250;

export class RaceTimeline {
  private frames: TimelineFrame[] = [];
  private raceId = -1;
  private offset: number | null = null;

  constructor(readonly delayMs = 160) {}

  /** A shared state arrived at local time `localNow`. `progress` is in decimetres; null = lined up at the start. */
  push(state: { raceId: number; clock: number; progress: number[] | null }, localNow: number): void {
    if (state.raceId !== this.raceId || state.progress === null) {
      this.frames = [];
      this.offset = null;
      this.raceId = state.raceId;
      if (state.progress === null) return;
    }
    const m = state.progress.map((d) => d / 10);
    const last = this.frames[this.frames.length - 1];
    if (last && state.clock < last.clock) return;
    if (last && state.clock === last.clock) last.m = m;
    else this.frames.push({ clock: state.clock, m });
    const o = localNow - state.clock;
    this.offset = this.offset === null || o < this.offset ? o : this.offset + (o - this.offset) * 0.05;
    const newest = this.frames[this.frames.length - 1]!.clock;
    while (this.frames.length > 2 && this.frames[0]!.clock < newest - KEEP_MS) this.frames.shift();
  }

  /** True while there are positions to draw (the race is on, or the horses are pulling up). */
  get running(): boolean {
    return this.frames.length > 0;
  }

  sample(localNow: number): TimelineSample | null {
    const f = this.frames;
    if (f.length === 0 || this.offset === null) return null;
    const clock = localNow - this.offset - this.delayMs;
    const first = f[0]!;
    const last = f[f.length - 1]!;
    if (f.length === 1 || clock <= first.clock) return { m: [...first.m], v: f.length > 1 ? speeds(first, f[1]!) : first.m.map(() => 0) };
    if (clock >= last.clock) {
      const prev = f[f.length - 2]!;
      const v = speeds(prev, last);
      const ahead = Math.min(clock - last.clock, MAX_EXTRAPOLATE_MS) / 1000;
      return { m: last.m.map((x, i) => x + Math.max(0, v[i]!) * ahead), v };
    }
    let i = 0;
    while (i + 2 < f.length && f[i + 1]!.clock <= clock) i++;
    const a = f[i]!;
    const b = f[i + 1]!;
    const t = (clock - a.clock) / Math.max(1, b.clock - a.clock);
    return { m: a.m.map((x, k) => x + (b.m[k]! - x) * t), v: speeds(a, b) };
  }
}

function speeds(a: TimelineFrame, b: TimelineFrame): number[] {
  const dt = Math.max(1, b.clock - a.clock) / 1000;
  return a.m.map((x, k) => Math.max(0, (b.m[k]! - x) / dt));
}
